import { useEffect, useState } from 'react';
import TopNavBar from '@/components/TopNavBar';
import SEO from '@/hooks/useSEO';
import { PricingTable } from '@clerk/clerk-react';
import { useNavigate } from 'react-router';
import { InAppBrowserWarning } from '@/components/InAppBrowserWarning';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { isInAppBrowser } from '@/utils/browserDetection';

export default function PricingPage() {
  const navigate = useNavigate();
  const [hasPaymentError, setHasPaymentError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);
  const showInAppWarning = isInAppBrowser();

  // Listen for Stripe/Clerk payment errors
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorMessage =
        typeof error === 'string' ? error : error?.message || '';

      // Check if this is a Stripe/Clerk payment error
      const isPaymentError =
        errorMessage.includes('stripe.confirmSetup') ||
        errorMessage.includes('Payment Element') ||
        errorMessage.includes('Express Checkout Element') ||
        errorMessage.includes('IntegrationError') ||
        errorMessage.includes('Stripe');

      if (isPaymentError) {
        console.error('Payment error detected:', error);
        setHasPaymentError(true);
        setErrorMessage(
          errorMessage ||
            'Es gab einen Fehler bei der Verarbeitung Ihrer Zahlung'
        );
        // Don't prevent default - let our global handler also process it
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  const handleRetry = () => {
    setIsRetrying(true);
    setHasPaymentError(false);
    setErrorMessage('');

    // Force a reload of the pricing table
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  return (
    <>
      <SEO
        title="Pricing – Unlock Unlimited German Practice"
        description="Upgrade to Pro for unlimited question practice, full article access, and advanced learning features. Start free, upgrade anytime."
        canonicalPath="/pricing"
      />
      <TopNavBar
        currentPage="dashboard"
        onNavigateToAdmin={() => navigate('/?view=admin')}
        onNavigateToArticles={() => navigate('/')}
        onNavigateToDashboard={() => navigate('/dashboard')}
        onNavigateToQuestions={() => navigate('/questions')}
      />
      <div className="min-h-screen bg-gradient-to-br from-background to-learning-muted/20 p-6">
        <div className="container max-w-7xl mx-auto">
          {/* Show in-app browser warning */}
          {showInAppWarning && (
            <div className="mb-6">
              <Alert className="border-orange-200 bg-orange-50">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
                <AlertTitle className="text-orange-900 font-semibold">
                  Zahlung funktioniert möglicherweise nicht in diesem Browser
                </AlertTitle>
                <AlertDescription className="text-orange-800">
                  <p className="text-sm mb-3">
                    Sie verwenden einen In-App-Browser, der Probleme mit
                    Zahlungen haben könnte. Für die beste Erfahrung öffnen Sie
                    diese Seite bitte im regulären Browser Ihres Telefons
                    (Safari oder Chrome).
                  </p>
                  <Button
                    onClick={() => {
                      // Versuchen, im Systembrowser zu öffnen
                      window.open(window.location.href, '_system');
                    }}
                    variant="outline"
                    size="sm"
                    className="text-orange-900 border-orange-300 hover:bg-orange-100"
                  >
                    Im Browser öffnen
                  </Button>
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Zahlungsfehler mit Wiederholungsoption anzeigen */}
          {hasPaymentError && (
            <div className="mb-6">
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <AlertTitle className="text-red-900 font-semibold">
                  Zahlungsfehler
                </AlertTitle>
                <AlertDescription className="text-red-800 space-y-3">
                  <p className="text-sm">{errorMessage}</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={handleRetry}
                      disabled={isRetrying}
                      variant="outline"
                      size="sm"
                      className="text-red-900 border-red-300 hover:bg-red-100"
                    >
                      {isRetrying ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Erneut versuchen...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Zahlung erneut versuchen
                        </>
                      )}
                    </Button>
                    {showInAppWarning && (
                      <Button
                        onClick={() =>
                          window.open(window.location.href, '_system')
                        }
                        variant="outline"
                        size="sm"
                        className="text-red-900 border-red-300 hover:bg-red-100"
                      >
                        Im regulären Browser öffnen
                      </Button>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          )}

          <PricingTable />
        </div>
      </div>
    </>
  );
}
